import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  CronChangeLog,
  CronJob,
  CronListParams,
  CronSubmitPayload,
  FailedJob,
  QueuedJob,
} from '../types'

/* ---------------- Queued jobs ---------------- */

export async function getQueuedJobs(): Promise<QueuedJob[]> {
  try {
    const response = await apiClient.get<QueuedJob[]>('/api/queued_jobs')
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load queued jobs')
  }
}

function optionalQueuedPath(id: number | undefined): string {
  return id != null ? `/api/queued_jobs/${id}` : '/api/queued_jobs'
}

export async function deleteQueuedJob(id?: number): Promise<void> {
  try {
    await apiClient.delete(optionalQueuedPath(id))
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete queued job')
  }
}

export async function resetQueuedJob(id?: number): Promise<void> {
  try {
    await apiClient.put(optionalQueuedPath(id), {})
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to reset queued job')
  }
}

/* ---------------- Failed jobs ---------------- */

export async function getFailedJobs(): Promise<FailedJob[]> {
  try {
    const response = await apiClient.get<FailedJob[]>('/api/failed_jobs')
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load failed jobs')
  }
}

function optionalFailedPath(id: number | undefined): string {
  return id != null ? `/api/failed_jobs/${id}` : '/api/failed_jobs'
}

export async function deleteFailedJob(id?: number): Promise<void> {
  try {
    await apiClient.delete(optionalFailedPath(id))
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete failed job')
  }
}

export async function retryFailedJob(id?: number): Promise<void> {
  try {
    await apiClient.put(optionalFailedPath(id), {})
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to retry failed job')
  }
}

/* ---------------- Cron jobs ---------------- */

export async function getCronJobs(params: CronListParams = {}): Promise<CronJob[]> {
  try {
    const response = await apiClient.get<CronJob[]>('/api/cron', { params })
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load cron jobs')
  }
}

/**
 * GET /api/cron/{id} — change-log entries for a single cron job. Mirrors
 * Angular `CronInfoUpdateComponent.getCronDetail()`.
 */
export async function getCronDetail(cronId: string | number): Promise<CronChangeLog[]> {
  try {
    const response = await apiClient.get<CronChangeLog[]>(`/api/cron/${cronId}`)
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load cron change log')
  }
}

export async function saveCronJob(
  payload: CronSubmitPayload,
  cronId?: string | number,
): Promise<CronJob> {
  try {
    const url = cronId ? `/api/cron/${cronId}` : '/api/cron'
    const response = await apiClient.post<CronJob>(url, payload)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save cron job')
  }
}

export async function toggleCronStatus(cronJob: CronJob): Promise<CronJob> {
  try {
    const nextStatus = cronJob.cron_status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    const response = await apiClient.post<CronJob>(`/api/cron/${cronJob.cron_id}`, {
      status: nextStatus,
      description: cronJob.cron_description,
    })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update cron status')
  }
}
